import React, { useState, useEffect } from 'react';
import { Card, Tag, Tooltip, Typography, Checkbox, Space, Button } from 'antd';
import { EyeOutlined, DownOutlined, UpOutlined, ShoppingCartOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { isEmpty } from 'lodash';
import CampaignResponsiveChart from './CampaignResponsiveChart';
import StatusTag, { getServedImpressions } from '../shared/StatusTag';
import { getPacingTone, getFlightPhase, PacingDot, PacingBar, BulletBar, RateBars, BudgetDonut, CumulativePacingChart, PACING_TONES, TREND_ARROWS } from '../shared/pacingVisuals';
import { hasSpecialCharacters, formatCampaignName } from '../../../../core/utils/campaignNaming';
import { useTimezone } from '../../../../core/hooks/useTimezone';
import {
  getPacingGoal,
  getDailyPacing,
  totalSpentFromAggregate,
  dailyTotalSpentFromAggregate,
  formatCurrency,
  formatWholeNumber,
  formatNumber2SigFig,
  getCTRFromAggregate,
  getEffectiveCpm
} from '../../../../core/utils/campaigns';
import moment from 'moment';

// Import the same SVG components used in the table
import { ReactComponent as ReactLogo } from '../../../../assets/icons/campaigns/retaillocation.svg';
import { ReactComponent as SignUpEnabled } from '../../../../assets/icons/campaigns/signup_2.svg';
import { ReactComponent as ShoppingCartBasketItem } from '../../../../assets/icons/campaigns/shopping-cart-basket-item.svg';
import { ReactComponent as ShoppingCartBasketItemLocation } from '../../../../assets/icons/campaigns/shopping-cart-basket-item-location.svg';

const { Text } = Typography;

const CampaignCard = ({
  campaign,
  currencyFormat,
  onCardClick,
  isSelected,
  onSelectionChange,
  selectedTableColumns,
  possibleFields,
  onToggleClassicPacing,
  onOpenPacingModal
}) => {
  const { timezone } = useTimezone();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isVerySmall, setIsVerySmall] = useState(window.innerWidth <= 480);
  const { aggregateData, pacingData, startDate, endDate, budget, name, status } = campaign;
  const cpm = getEffectiveCpm(campaign);

  // Handle responsive resizing
  useEffect(() => {
    const handleResize = () => {
      setIsVerySmall(window.innerWidth <= 480);
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Format campaign name
  const displayName = !hasSpecialCharacters(name) ? formatCampaignName(campaign, timezone) : name;
  
  // Get pacing information — flight-window entry (today) is the source of
  // truth, same as the table cell and the View More modal; the scalar
  // pacingData.pace/days* fields can diverge (order vs flight dates).
  const paceEntry = pacingData?.pacing?.[0];
  const pace = paceEntry?.pace ?? pacingData?.pace ?? 0;

  // ── Pacing UX (DASHBOARD_PACING_UX_SPEC) ──────────────────────────────
  // pace is a flight-END projection, not a position. Same framing as the
  // queue's In-Flight view: muted during the unstable early window, math on
  // hover, red only once the flight is mature. Shares the classic-view
  // toggle (localStorage) so one preference governs both pages.
  const classicPacing = localStorage.getItem('pacingClassicView') === '1';
  const { daysIntoFlight, daysLeft, flightLength, unstableDays, isRamping } = getFlightPhase(
    paceEntry ?? pacingData
  );
  const flightTooYoung = !classicPacing && isRamping;
  const pacingTrend =
    pacingData?.paceYesterday != null && pacingData?.paceSevenDaysAgo != null
      ? pacingData.paceYesterday > pacingData.paceSevenDaysAgo
        ? 'rising'
        : pacingData.paceYesterday < pacingData.paceSevenDaysAgo
          ? 'falling'
          : 'steady'
      : null;
  const pacingRatePerDay = pacingData?.dailyAvgImpressions || pacingData?.impressionsYesterday || 0;
  const pacingNeededPerDay = daysLeft > 0 ? (pacingData?.impressionsLeft || 0) / daysLeft : 0;
    const pacingTooltip = (
    <div style={{ minWidth: 220 }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>Day {daysIntoFlight} of {flightLength}</div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'auto 1fr',
          columnGap: 12,
          rowGap: 2,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <span style={{ opacity: 0.75 }}>Delivering</span>
        <span style={{ textAlign: 'right' }}>{formatWholeNumber(pacingRatePerDay)}/day</span>
        <span style={{ opacity: 0.75 }}>Needed</span>
        <span style={{ textAlign: 'right' }}>{formatWholeNumber(pacingNeededPerDay)}/day</span>
        <span style={{ opacity: 0.75 }}>Days left</span>
        <span style={{ textAlign: 'right' }}>{daysLeft}</span>
        {pacingTrend && (
          <React.Fragment>
            <span style={{ opacity: 0.75 }}>Trend</span>
            <span style={{ textAlign: 'right' }}>{pacingTrend}</span>
          </React.Fragment>
        )}
      </div>
  {flightTooYoung && pace > 0 && (
    <div style={{ marginTop: 4 }}>
      Current unstable projection: {formatNumber2SigFig(pace)}% — extrapolated from
      {' '}{daysIntoFlight} day{daysIntoFlight === 1 ? '' : 's'} of data
    </div>
  )}
      <div style={{ opacity: 0.7, marginTop: 6, fontSize: 12 }}>
        Projected finish = (recent rate x days left + delivered) / flight goal
      </div>
    </div>
  );
  
  // Get status information
  const statusInfo = {
    startDate: startDate ? moment(startDate).seconds(0).milliseconds(0).toISOString() : 'N/A',
    endDate: endDate ? moment(endDate).seconds(0).milliseconds(0).toISOString() : 'N/A',
    attributionWindow: campaign.attributionWindow || 'N/A',
    statusCampaign: status,
    impressions: getServedImpressions(campaign)
  };

  // Get spending information
  const overallTotalSpent = totalSpentFromAggregate(aggregateData);
  const dailyTotalSpent = dailyTotalSpentFromAggregate(aggregateData);
  const pacingGoals = getPacingGoal(startDate, endDate, budget);
  const dailyPacing = getDailyPacing(pacingGoals, dailyTotalSpent);

  // Get additional metrics for bottom section
  const transactionsCount = aggregateData?.overallData?.aggEvents?.transactionsTotalCount || 0;
  const revenue = aggregateData?.overallData?.aggEvents?.transactionTotalRevenue || 0;
  const signUpsCount = aggregateData?.overallData?.signupsOverallData || 0;
  const roas = (() => {
    if (campaign.pricingModel === 'Percentage') {
      return overallTotalSpent > 0 ? (revenue / overallTotalSpent) * 100 : 0;
    } else {
      const impressions = aggregateData?.overallData?.aggImpressions || 0;
      const spend = cpm * (impressions / 1000);
      return spend > 0 ? (revenue / spend) * 100 : 0;
    }
  })();

  // Check if we should show sales and signups based on column selection
  const showSales = selectedTableColumns && selectedTableColumns.includes('Sales');
  const showSignUps = selectedTableColumns && selectedTableColumns.includes('Sign Ups');

  // Determine pacing tone — calm palette (pacingVisuals): classic view keeps
  // the legacy colored text, the new view uses neutral ink + dot + bar.
  // Percentage campaigns use the budget gates (50–100 on-track, >100
  // overspend) so a fast burner never reads "on track".
  const isPercentage = campaign.pricingModel === 'Percentage';
  const pacingGracePeriod = !classicPacing && pace > 0 && pace < 91 && daysIntoFlight < 10;
  const pacingTone = flightTooYoung
    ? PACING_TONES.neutral
    : getPacingTone(pace, { isPercentage, gracePeriod: pacingGracePeriod });
  const pacingColor = classicPacing ? (pace >= 95 ? '#52c41a' : pace >= 91 ? '#faad14' : '#ff4d4f') : pacingTone;

  // Visualization inputs (new view): goal from budget/CPM, delivered and
  // spend from the aggregate totals. Impression visuals are impression-
  // campaigns-only — percentage campaigns are budget-paced.
  const impressionGoal = !isPercentage && cpm && budget && budget !== 1 ? (budget / cpm) * 1000 : 0;
  const deliveredImpressions = aggregateData?.overallData?.aggImpressions || 0;
  const deliveredPct = impressionGoal > 0 ? (deliveredImpressions / impressionGoal) * 100 : 0;
  const overallTotalSpentForDonut = totalSpentFromAggregate(aggregateData);
  const spendPct = budget > 0 ? (overallTotalSpentForDonut / budget) * 100 : 0;

  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick(campaign);
    }
  };

  const handleSelectionClick = (e) => {
    e.stopPropagation();
    if (onSelectionChange) {
      onSelectionChange(campaign);
    }
  };

  const handleExpandClick = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  // Helper function to get the latest transaction date
  const getLatestTransactionDate = (aggregateData) => {
    const transactions = aggregateData?.overallData?.aggEvents?.transactionsByDate;
    if (!transactions?.length) return null;

    const latestTransaction = transactions
      .filter((transaction) => transaction?.transactionsTotalCount > 0 && transaction?.xAxis)
      .reduce((latest, current) => {
        const currentDate = moment(current.xAxis);
        const latestDate = latest ? moment(latest.xAxis) : null;
        return !latestDate || currentDate.isAfter(latestDate) ? current : latest;
      }, null);

    return latestTransaction?.xAxis ? moment(latestTransaction.xAxis).format('MM/DD/YY') : null;
  };

  // Render expanded field data based on column type - Enhanced with dual-state display
  const renderExpandedField = (field, campaign) => {
    const { aggregateData, pacingData, startDate, endDate, budget } = campaign;
    const cpm = getEffectiveCpm(campaign);

    switch (field.title) {
      case 'Days Remaining':
        // Enhanced Days Remaining with visual progress and context - standardized colors
        const daysLeft = pacingData?.daysLeft;
        let remainingDays = 0;
        
        if (daysLeft !== undefined && daysLeft !== null) {
          remainingDays = daysLeft;
        } else {
          const endDateMoment = moment(endDate).startOf('day');
          const today = moment().startOf('day');
          if (endDateMoment.isBefore(today)) {
            remainingDays = 0;
          } else {
            remainingDays = endDateMoment.diff(today, 'days');
          }
        }

        // Calculate campaign progress percentage
        const totalCampaignDays = startDate && endDate ? moment(endDate).diff(moment(startDate), 'days') : 0;
        const daysElapsed = totalCampaignDays > 0 ? totalCampaignDays - remainingDays : 0;
        const progressPercentage = totalCampaignDays > 0 ? (daysElapsed / totalCampaignDays) * 100 : 0;

        // Color logic: blue when active (>0 days), dark bold when completed (0 days)
        const isActive = remainingDays > 0;
        const mainColor = isActive ? '#1890ff' : '#262626'; // Blue when active, dark bold when completed
        const progressBarColor = isActive ? '#1890ff' : '#8c8c8c'; // Blue when active, neutral when completed

        return (
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            padding: '8px 0'
          }}>
            {/* Large centered number */}
            <Text style={{ 
              fontSize: '24px', 
              fontWeight: 'bold',
              color: mainColor,
              lineHeight: '1.2',
              marginBottom: '4px'
            }}>
              {remainingDays}
            </Text>
            
            {/* Progress bar and additional context */}
            <div style={{ width: '100%', textAlign: 'center' }}>
              <div style={{ 
                height: '4px', 
                backgroundColor: '#e8e8e8', 
                borderRadius: '2px',
                margin: '4px 0',
                overflow: 'hidden'
              }}>
                <div style={{ 
                  height: '100%', 
                  backgroundColor: progressBarColor,
                  width: `${Math.min(progressPercentage, 100)}%`,
                  borderRadius: '2px'
                }} />
              </div>
              <Text style={{ fontSize: '10px', color: '#8c8c8c' }}>
                {progressPercentage > 0 ? `${Math.round(progressPercentage)}% complete` : 'Starting soon'}
              </Text>
            </div>
          </div>
        );

      case 'Start Date':
        // Enhanced Start Date with calendar context - standardized dark bold text
        if (startDate) {
          const startMoment = moment(startDate);
          const today = moment();
          const daysSinceStart = today.diff(startMoment, 'days');
          const isFuture = startMoment.isAfter(today);
          
          return (
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '8px 0'
            }}>
              {/* Large formatted date - dark bold text */}
              <Text style={{ 
                fontSize: '16px', 
                fontWeight: 'bold',
                color: '#262626', // Dark bold text
                lineHeight: '1.2',
                marginBottom: '2px'
              }}>
                {startMoment.format('MM/DD/YY')}
              </Text>
              
              {/* Additional context */}
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: '10px', color: '#8c8c8c' }}>
                  {isFuture ? `In ${daysSinceStart * -1} days` : `${daysSinceStart} days ago`}
                </Text>
                <br />
                <Text style={{ fontSize: '10px', color: '#8c8c8c' }}>
                  {startMoment.format('dddd')}
                </Text>
              </div>
            </div>
          );
        }
        return (
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}>
            <Text style={{ 
              fontSize: '16px', 
              fontWeight: 'bold',
              color: '#d9d9d9',
              lineHeight: '1.2'
            }}>
              N/A
            </Text>
          </div>
        );

      case 'End Date':
        // Enhanced End Date with calendar context - standardized dark bold text
        if (endDate) {
          const endMoment = moment(endDate);
          const today = moment();
          const daysUntilEnd = endMoment.diff(today, 'days');
          const isPast = endMoment.isBefore(today);
          
          return (
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '8px 0'
            }}>
              {/* Large formatted date - dark bold text */}
              <Text style={{ 
                fontSize: '16px', 
                fontWeight: 'bold',
                color: '#262626', // Dark bold text
                lineHeight: '1.2',
                marginBottom: '2px'
              }}>
                {endMoment.format('MM/DD/YY')}
              </Text>
              
              {/* Additional context */}
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: '10px', color: '#8c8c8c' }}>
                  {isPast ? 'Ended' : `${daysUntilEnd} days left`}
                </Text>
                <br />
                <Text style={{ fontSize: '10px', color: '#8c8c8c' }}>
                  {endMoment.format('dddd')}
                </Text>
              </div>
            </div>
          );
        }
        return (
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}>
            <Text style={{ 
              fontSize: '16px', 
              fontWeight: 'bold',
              color: '#d9d9d9',
              lineHeight: '1.2'
            }}>
              N/A
            </Text>
          </div>
        );

      case 'Budget':
        // Responsive Budget display - simplified for very small screens
        if (budget && cpm) {
          let impressionGoal = (budget / cpm) * 1000;
          let impressions = 0;
          try {
            impressions = aggregateData.overallData.aggImpressions;
          } catch (e) {
            impressions = 0;
          }
          const budgetSpent = cpm * (impressions / 1000);
          const budgetRemaining = budget - budgetSpent;
          const budgetRemainingPercentage = (budgetRemaining / budget) * 100;
          const percentageProgress = 100 - budgetRemainingPercentage;

          if (isVerySmall) {
            // Simplified single-column layout for very small screens
            return (
              <div style={{ 
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                fontSize: '11px'
              }}>
                <div style={{ 
                  padding: '8px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px'
                }}>
                  <Text strong style={{ fontSize: '11px', color: '#8c8c8c', marginBottom: '4px' }}>Budget Summary</Text>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <Text style={{ fontSize: '11px' }}>Spent:</Text>
                    <Text strong style={{ fontSize: '11px' }}>{formatCurrency(budgetSpent, currencyFormat)}</Text>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <Text style={{ fontSize: '11px' }}>Remaining:</Text>
                    <Text strong style={{ fontSize: '11px' }}>{formatCurrency(budgetRemaining, currencyFormat)}</Text>
                  </div>
                  <div style={{ 
                    height: '8px', 
                    backgroundColor: '#e8e8e8', 
                    borderRadius: '4px',
                    margin: '6px 0',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      height: '100%', 
                      backgroundColor: percentageProgress >= 100 ? '#52c41a' : '#1890ff',
                      width: `${Math.min(percentageProgress, 100)}%`,
                      borderRadius: '4px'
                    }} />
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text style={{ fontSize: '10px', color: '#8c8c8c' }}>Progress</Text>
                    <Text strong style={{ fontSize: '10px', color: '#262626' }}>
                      {formatNumber2SigFig(percentageProgress)}%
                    </Text>
                  </div>
                </div>
              </div>
            );
          }

          // Original 2-column layout for larger screens
          return (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '8px',
              fontSize: '10px'
            }}>
              {/* Details View */}
              <div style={{ 
                padding: '6px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px'
              }}>
                <Text strong style={{ fontSize: '10px', color: '#8c8c8c' }}>Details</Text>
                <div style={{ marginTop: '4px' }}>
                  <Text style={{ fontSize: '10px' }}>Budget: {formatCurrency(budget, currencyFormat)}</Text>
                  <br />
                  <Text style={{ fontSize: '10px' }}>Imp Goal: {formatWholeNumber(impressionGoal)}</Text>
                  <br />
                  <Text style={{ fontSize: '10px' }}>CPM: {formatCurrency(cpm, currencyFormat)}</Text>
                </div>
              </div>

              {/* Progress View */}
              <div style={{ 
                padding: '6px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px'
              }}>
                <Text strong style={{ fontSize: '10px', color: '#8c8c8c' }}>Progress</Text>
                <div style={{ marginTop: '4px' }}>
                  <Text style={{ fontSize: '10px' }}>
                    {formatCurrency(budgetSpent, currencyFormat)} spent of {formatCurrency(budget, currencyFormat)}
                  </Text>
                  <div style={{ 
                    height: '6px', 
                    backgroundColor: '#e8e8e8', 
                    borderRadius: '3px',
                    margin: '4px 0',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      height: '100%', 
                      backgroundColor: percentageProgress >= 100 ? '#52c41a' : '#1890ff',
                      width: `${Math.min(percentageProgress, 100)}%`,
                      borderRadius: '3px'
                    }} />
                  </div>
                  <Text style={{ fontSize: '10px' }}>
                    Remaining: {formatCurrency(budgetRemaining, currencyFormat)}
                  </Text>
                </div>
              </div>
            </div>
          );
        }
        return <Text style={{ fontSize: '11px' }}>N/A</Text>;

      case 'Performance':
        // Single-line three-row layout for Performance metrics - CENTERED
        if (!isEmpty(aggregateData)) {
          const impressions = aggregateData.overallData.aggImpressions || 0;
          const clicks = aggregateData.overallData.aggClicks || 0;
          const ctr = getCTRFromAggregate(aggregateData.overallData);
          
          return (
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '8px 0'
            }}>
              {/* Impressions row - CENTERED */}
              <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                <Text style={{ fontSize: '14px', fontWeight: 'bold', color: '#1890ff' }}>
                  {formatWholeNumber(impressions)}
                </Text>
                <Text style={{ fontSize: '11px', color: '#8c8c8c', marginLeft: '4px' }}>Imps</Text>
              </div>
              
              {/* Clicks row - CENTERED */}
              <div style={{ textAlign: 'center', marginBottom: '4px' }}>
                <Text style={{ fontSize: '14px', fontWeight: 'bold', color: '#52c41a' }}>
                  {formatWholeNumber(clicks)}
                </Text>
                <Text style={{ fontSize: '11px', color: '#8c8c8c', marginLeft: '4px' }}>Clicks</Text>
              </div>
              
              {/* CTR row - CENTERED */}
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: '14px', fontWeight: 'bold', color: ctr > 0.5 ? '#52c41a' : '#faad14' }}>
                  {ctr}%
                </Text>
                <Text style={{ fontSize: '11px', color: '#8c8c8c', marginLeft: '4px' }}>CTR</Text>
              </div>
            </div>
          );
        }
        return (
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}>
            <Text style={{ 
              fontSize: '14px', 
              fontWeight: 'bold',
              color: '#d9d9d9',
              lineHeight: '1.2'
            }}>
              N/A
            </Text>
          </div>
        );

      case 'Pacing':
        // Dual-state display for Pacing (Today vs Yesterday)
        if (pacingData && aggregateData && !isEmpty(aggregateData)) {
          const { byDate } = aggregateData;
          const chartData = byDate;
          
          if (byDate && !isEmpty(byDate)) {
            const paceToday = pacingData.pace || 0;
            const paceYesterday = pacingData.paceYesterday || 0;
            const daysLeftToday = pacingData.daysLeft || 0;
            const daysLeftYesterday = daysLeftToday + 1; // Yesterday's view adds 1 day
            
            // Calculate impressions data for both views
            let impressionsSpentToday = 0;
            let impressionsSpentYesterday = 0;
            let todaysImpressions = 0;
            let yesterdaysImpressions = pacingData.impressionsYesterday || 0;
            
            if (aggregateData && aggregateData.overallData) {
              const latestDate = chartData.reduce((latest, curr) => {
                const { xAxis } = curr;
                if (!latest) {
                  return moment(xAxis, 'YYYY-MM-DD').format('YYYY-MM-DD');
                }
                if (moment(xAxis, 'YYYY-MM-DD').isAfter(latest, 'day')) {
                  return moment(xAxis, 'YYYY-MM-DD').format('YYYY-MM-DD');
                }
                return latest;
              }, moment(startDate, 'YYYY-MM-DD').format('YYYY-MM-DD'));

              impressionsSpentToday = chartData.reduce((acc, curr) => {
                const { xAxis, impressions } = curr;
                const dateToday = moment(latestDate, 'YYYY-MM-DD').subtract(1, 'days');
                if (moment(xAxis, 'YYYY-MM-DD').isSameOrBefore(dateToday, 'day')) {
                  acc += impressions;
                }
                return acc;
              }, 0);

              impressionsSpentYesterday = chartData.reduce((acc, curr) => {
                const { xAxis, impressions } = curr;
                const dateYesterday = moment(latestDate, 'YYYY-MM-DD').subtract(2, 'days');
                if (moment(xAxis, 'YYYY-MM-DD').isSameOrBefore(dateYesterday, 'day')) {
                  acc += impressions;
                }
                return acc;
              }, 0);

              todaysImpressions = chartData[chartData.length - 1]?.impressions || 0;
            }

            let impressionGoal = 0;
            if (cpm && budget && budget !== 1) {
              impressionGoal = (budget / cpm) * 1000;
            }

            const impressionsLeftToday = impressionGoal - impressionsSpentToday;
            const impressionsLeftYesterday = impressionGoal - impressionsSpentYesterday;
            
            const dailyImpressionsGoalToday = daysLeftToday > 0 ? impressionsLeftToday / daysLeftToday : 0;
            const dailyImpressionsGoalYesterday = daysLeftYesterday > 0 ? impressionsLeftYesterday / daysLeftYesterday : 0;
            const yesterdayTone = getPacingTone(paceYesterday, {
              isPercentage,
              gracePeriod: pacingGracePeriod,
            });

            return (
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr', 
                gap: '8px',
                fontSize: '10px'
              }}>
                {/* Today View */}
                <div style={{ 
                  padding: '6px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px'
                }}>
                  <Text strong style={{ fontSize: '10px', color: '#8c8c8c' }}>Today</Text>
                  <div style={{ marginTop: '4px' }}>
                    <Text style={{ fontSize: '10px', display: 'inline-flex', alignItems: 'center' }}>
                      {classicPacing ? 'Pace:' : 'Projected finish:'}{' '}
                      {!classicPacing && <PacingDot tone={pacingTone} />}
                      <span style={{ color: flightTooYoung ? '#595959' : classicPacing ? pacingColor : PACING_TONES.ink, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {flightTooYoung ? '—' : `${formatNumber2SigFig(paceToday)}%`}
                      </span>
                      {!classicPacing && pacingTrend && !flightTooYoung && (
                        <span style={{ marginLeft: 3, color: PACING_TONES.neutral }}>{TREND_ARROWS[pacingTrend]}</span>
                      )}
                    </Text>
                    {!classicPacing && (
                      <PacingBar pace={paceToday} tooYoung={flightTooYoung} tone={pacingTone} height={5} style={{ marginTop: 4 }} />
                    )}
                    <br />
                    {!classicPacing && (
                      <>
                        <Text style={{ fontSize: '10px' }}>Delivering: {formatWholeNumber(pacingRatePerDay)}/day</Text>
                        <br />
                        <Text style={{ fontSize: '10px' }}>Needed: {formatWholeNumber(pacingNeededPerDay)}/day</Text>
                        <br />
                      </>
                    )}
                    {classicPacing && (
                      <>
                        <Text style={{ fontSize: '10px' }}>Daily: {formatWholeNumber(dailyImpressionsGoalToday)}</Text>
                        <br />
                      </>
                    )}
                    <Text style={{ fontSize: '10px' }}>Remaining: {formatWholeNumber(impressionsLeftToday)}</Text>
                    <br />
                    <Text style={{ fontSize: '10px' }}>Today: {formatWholeNumber(todaysImpressions)}</Text>
                    <br />
                    <Text style={{ fontSize: '10px' }}>Served: {formatWholeNumber(impressionsSpentToday)}</Text>
                  </div>
                </div>

                {/* Yesterday View */}
                <div style={{
                  padding: '6px',
                  backgroundColor: '#f8f9fa',
                  borderRadius: '4px'
                }}>
                  <Text strong style={{ fontSize: '10px', color: '#8c8c8c' }}>Yesterday</Text>
                  <div style={{ marginTop: '4px' }}>
                    <Text style={{ fontSize: '10px', display: 'inline-flex', alignItems: 'center' }}>
                      {classicPacing ? 'Pace:' : 'Projected finish:'}{' '}
                      {!classicPacing && <PacingDot tone={yesterdayTone} />}
                      <span style={{ color: classicPacing ? pacingColor : PACING_TONES.ink, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                        {`${formatNumber2SigFig(paceYesterday)}%`}
                      </span>
                    </Text>
                    {!classicPacing && (
                      <PacingBar pace={paceYesterday} tooYoung={false} tone={yesterdayTone} height={5} style={{ marginTop: 4 }} />
                    )}
                    <br />
                    <Text style={{ fontSize: '10px' }}>Remaining: {formatWholeNumber(impressionsLeftYesterday)}</Text>
                    <br />
                    <Text style={{ fontSize: '10px' }}>Daily: {formatWholeNumber(dailyImpressionsGoalYesterday)}</Text>
                    <br />
                    <Text style={{ fontSize: '10px' }}>Yesterday: {formatWholeNumber(yesterdaysImpressions)}</Text>
                    <br />
                    <Text style={{ fontSize: '10px' }}>Served: {formatWholeNumber(impressionsSpentYesterday)}</Text>
                  </div>
                </div>
              </div>
            );
          }
        }
        return <Text style={{ fontSize: '11px' }}>N/A</Text>;

      case 'Sales':
        // Dual-state display for Sales (Details vs Enhanced Metrics)
        if (!isEmpty(aggregateData)) {
          const orders = aggregateData.overallData.aggEvents?.transactionsTotalCount || 0;
          const revenue = aggregateData.overallData.aggEvents?.transactionTotalRevenue || 0;
          const latestTransactionDate = getLatestTransactionDate(aggregateData);
          const hasChartData = aggregateData.overallData.aggEvents?.transactionsByDate?.length > 0;
          
          let impressions = 0;
          try {
            impressions = aggregateData.overallData.aggImpressions;
          } catch (e) {
            impressions = 0;
          }
          const budgetSpent = campaign.pricingModel === 'Percentage'
            ? totalSpentFromAggregate(aggregateData)
            : cpm * (impressions / 1000);
          const roas = budgetSpent > 0 ? (revenue / budgetSpent) * 100 : 0;

          // Calculate enhanced metrics
          const avgOrderValue = orders > 0 ? revenue / orders : 0;
          const conversionRate = impressions > 0 ? (orders / impressions) * 100 : 0;
          
          // Calculate campaign duration for daily metrics
          const campaignStart = startDate ? moment(startDate) : moment();
          const campaignEnd = endDate ? moment(endDate) : moment();
          const campaignDays = Math.max(1, campaignEnd.diff(campaignStart, 'days'));
          const dailyRevenue = revenue / campaignDays;
          const dailyOrders = orders / campaignDays;

          return (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 1fr', 
              gap: '8px',
              fontSize: '10px'
            }}>
              {/* Details View */}
              <div style={{ 
                padding: '6px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px'
              }}>
                <Text strong style={{ fontSize: '10px', color: '#8c8c8c' }}>Details</Text>
                <div style={{ marginTop: '4px' }}>
                  <Text style={{ fontSize: '10px' }}>Orders: {orders}</Text>
                  <br />
                  <Text style={{ fontSize: '10px' }}>Rev: {formatCurrency(revenue, currencyFormat)}</Text>
                  <br />
                  <Text style={{ fontSize: '10px' }}>ROAS: {roas > 0 ? `${formatNumber2SigFig(roas)}%` : 'N/A'}</Text>
                  <br />
                  <Text style={{ fontSize: '10px' }}>Latest: {latestTransactionDate || 'N/A'}</Text>
                </div>
              </div>

              {/* Chart/Enhanced Metrics View */}
              <div style={{ 
                padding: '6px',
                backgroundColor: '#f8f9fa',
                borderRadius: '4px'
              }}>
                {hasChartData ? (
                  <>
                    <Text strong style={{ fontSize: '10px', color: '#8c8c8c' }}>Chart</Text>
                    <div style={{ marginTop: '4px', width: '100%', height: '40px' }}>
                      <CampaignResponsiveChart
                        data={aggregateData.overallData.aggEvents.transactionsByDate}
                        yAxisLeft="transactionsTotalCount"
                        height={35}
                        width={120}
                      />
                    </div>
                    {latestTransactionDate && (
                      <Text style={{ fontSize: '9px', color: '#8c8c8c', marginTop: '2px' }}>
                        Latest: {latestTransactionDate}
                      </Text>
                    )}
                  </>
                ) : (
                  <>
                    <Text strong style={{ fontSize: '10px', color: '#8c8c8c' }}>Metrics</Text>
                    <div style={{ marginTop: '4px', width: '100%' }}>
                      <Text style={{ fontSize: '10px' }}>
                        Avg Order: {avgOrderValue > 0 ? formatCurrency(avgOrderValue, currencyFormat) : 'N/A'}
                      </Text>
                      <br />
                      <Text style={{ fontSize: '10px' }}>
                        Conversion: {conversionRate > 0 ? `${formatNumber2SigFig(conversionRate)}%` : 'N/A'}
                      </Text>
                      <br />
                      <Text style={{ fontSize: '10px' }}>
                        Daily Rev: {dailyRevenue > 0 ? formatCurrency(dailyRevenue, currencyFormat) : 'N/A'}
                      </Text>
                      <br />
                      <Text style={{ fontSize: '10px' }}>
                        Daily Orders: {dailyOrders > 0 ? formatNumber2SigFig(dailyOrders) : 'N/A'}
                      </Text>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        }
        return <Text style={{ fontSize: '11px' }}>N/A</Text>;

      case 'Sign Ups':
        // Simplified Sign Ups display - just the large number like the full table
        if (!isEmpty(aggregateData)) {
          const signups = aggregateData.overallData.signupsOverallData || 0;

          return (
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              padding: '8px 0'
            }}>
              {/* Large centered number */}
              <Text style={{ 
                fontSize: '24px', 
                fontWeight: 'bold',
                color: '#1890ff',
                lineHeight: '1.2'
              }}>
                {formatWholeNumber(signups)}
              </Text>
            </div>
          );
        }
        return (
          <div style={{ 
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%'
          }}>
            <Text style={{ 
              fontSize: '24px', 
              fontWeight: 'bold',
              color: '#d9d9d9',
              lineHeight: '1.2'
            }}>
              0
            </Text>
          </div>
        );

      case 'Attribution':
        // Use the same icon logic as the table view, but scaled to fill space
        const { transactionTarget, transactionsConfig, signUpCampaign, sessionConfig } = campaign;

        // Enhanced icon styling to fill available space
        const containerStyle = {
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '80px',
          width: '100%',
          gap: '12px'
        };

        // Enhanced icon sizes to fill space proportionally
        const iconSize = '2.5rem';
        const enhancedOpacity = '1.0';
        const disabledOpacity = '0.3';

        // Transaction icon logic with enhanced sizing
        const transactionIcon = () => {
          if (transactionTarget) {
            if (
              transactionsConfig &&
              transactionsConfig.traffId &&
              transactionsConfig.tracksBasketItems
            ) {
              return (
                <Tooltip title="Location-based basket tracking enabled">
                  <span
                    role="img"
                    className="anticon"
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <ShoppingCartBasketItemLocation
                      style={{
                        width: iconSize,
                        height: 'auto',
                        opacity: enhancedOpacity,
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                      }}
                    />
                  </span>
                </Tooltip>
              );
            }
            if (transactionsConfig && transactionsConfig.tracksBasketItems) {
              return (
                <Tooltip title="Basket item tracking enabled">
                  <span
                    role="img"
                    className="anticon"
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <ShoppingCartBasketItem
                      style={{
                        width: iconSize,
                        height: 'auto',
                        opacity: enhancedOpacity,
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                      }}
                    />
                  </span>
                </Tooltip>
              );
            }

            if (transactionsConfig && transactionsConfig.traffId) {
              return (
                <Tooltip title="Traffic ID tracking enabled">
                  <span
                    role="img"
                    className="anticon"
                    style={{ 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    <ReactLogo
                      style={{
                        width: iconSize,
                        height: 'auto',
                        opacity: enhancedOpacity,
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                      }}
                    />
                  </span>
                </Tooltip>
              );
            } else {
              return (
                <Tooltip title="Transaction tracking enabled">
                  <ShoppingCartOutlined
                    style={{
                      fontSize: iconSize,
                      opacity: enhancedOpacity,
                      filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
                    }}
                  />
                </Tooltip>
              );
            }
          } else {
            return (
              <Tooltip title="Transaction tracking disabled">
                <ShoppingCartOutlined
                  style={{
                    fontSize: iconSize,
                    opacity: disabledOpacity,
                  }}
                />
              </Tooltip>
            );
          }
        };

        // Enhanced Sign Up icon with tooltip
        const signUpIcon = () => (
          <Tooltip title={signUpCampaign ? "Sign-up tracking enabled" : "Sign-up tracking disabled"}>
            <SignUpEnabled
              style={{
                width: iconSize,
                height: 'auto',
                opacity: signUpCampaign ? enhancedOpacity : disabledOpacity,
                filter: signUpCampaign ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' : 'none'
              }}
            />
          </Tooltip>
        );

        // Enhanced Sessions icon with tooltip
        const sessionsIcon = () => (
          <Tooltip title={sessionConfig && sessionConfig.isSession ? "Session tracking enabled" : "Session tracking disabled"}>
            <EyeOutlined
              style={{
                fontSize: iconSize,
                opacity: sessionConfig && sessionConfig.isSession ? enhancedOpacity : disabledOpacity,
                filter: sessionConfig && sessionConfig.isSession ? 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))' : 'none'
              }}
            />
          </Tooltip>
        );

        return (
          <div style={containerStyle}>
            {transactionIcon()}
            {signUpIcon()}
            {sessionsIcon()}
          </div>
        );

      default:
        // Handle generic fields
        if (field.dataAccessor && aggregateData?.overallData?.[field.dataAccessor] !== undefined) {
          return (
            <Text style={{ fontSize: '11px' }}>
              {formatWholeNumber(aggregateData.overallData[field.dataAccessor])}
            </Text>
          );
        }
        return <Text style={{ fontSize: '11px' }}>N/A</Text>;
    }
  };

  return (
    <Card 
      size="small"
      className="campaign-card"
      style={{ 
        marginBottom: '16px',
        border: isSelected ? '2px solid #1890ff' : '1px solid #e8e8e8',
        borderRadius: '12px',
        cursor: 'pointer',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        position: 'relative',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 4px rgba(0, 0, 0, 0.08)',
        overflow: 'hidden'
      }}
      onClick={handleCardClick}
      bodyStyle={{ padding: '16px' }}
    >
      {/* Selection Checkbox */}
      <div 
        style={{ 
          position: 'absolute',
          top: '8px',
          left: '8px',
          zIndex: 10
        }}
        onClick={handleSelectionClick}
      >
        <Checkbox 
          checked={isSelected}
          onChange={handleSelectionClick}
          onClick={(e) => e.stopPropagation()}
        />
      </div>

      {/* Expand Chevron */}
      <div 
        style={{ 
          position: 'absolute',
          top: '8px',
          right: '8px',
          zIndex: 10,
          cursor: 'pointer'
        }}
        onClick={handleExpandClick}
      >
        {isExpanded ? (
          <UpOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
        ) : (
          <DownOutlined style={{ fontSize: '12px', color: '#8c8c8c' }} />
        )}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px', marginLeft: '24px', marginRight: '24px' }}>
        {/* Campaign Name */}
        <Tooltip title={displayName}>
          <Text 
            strong 
            style={{ 
              fontSize: '14px',
              lineHeight: '1.2',
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              flex: 1,
              marginRight: '8px'
            }}
          >
            {displayName}
          </Text>
        </Tooltip>

        {/* Status */}
        <div style={{ flexShrink: 0 }}>
          <StatusTag {...statusInfo} />
        </div>
      </div>

      {/* Responsive Chart & Performance Data Layout */}
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column',
        gap: '8px',
        marginBottom: '16px'
      }}>
        {/* Expanded Chart Section - Full width for better visibility */}
        <div>
          {aggregateData?.byDate ? (
            <div style={{ height: '45px' }}>
              <CampaignResponsiveChart
                data={aggregateData.byDate}
                yAxisLeft="impressions"
                height={45}
              />
            </div>
          ) : (
            <Text type="secondary" style={{ fontSize: '12px' }}>No impression data</Text>
          )}
        </div>

        {/* Compact Performance Metrics - Horizontal layout for space efficiency */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-around',
          alignItems: 'center',
          backgroundColor: '#f8f9fa',
          borderRadius: '4px',
          padding: '8px'
        }}>
          {!isEmpty(aggregateData) ? (
            <>
              {/* Impressions */}
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: '14px', fontWeight: 'bold', color: '#1890ff' }}>
                  {formatWholeNumber(aggregateData.overallData.aggImpressions)}
                </Text>
                <Text style={{ fontSize: '11px', color: '#8c8c8c', marginLeft: '4px' }}>
                  Imps
                </Text>
              </div>
              
              {/* Clicks */}
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: '14px', fontWeight: 'bold', color: '#52c41a' }}>
                  {formatWholeNumber(aggregateData.overallData.aggClicks)}
                </Text>
                <Text style={{ fontSize: '11px', color: '#8c8c8c', marginLeft: '4px' }}>
                  Clicks
                </Text>
              </div>
              
              {/* CTR */}
              <div style={{ textAlign: 'center' }}>
                <Text style={{ fontSize: '14px', fontWeight: 'bold', color: getCTRFromAggregate(aggregateData.overallData) > 0.5 ? '#52c41a' : '#faad14' }}>
                  {getCTRFromAggregate(aggregateData.overallData)}%
                </Text>
                <Text style={{ fontSize: '11px', color: '#8c8c8c', marginLeft: '4px' }}>
                  CTR
                </Text>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center', width: '100%' }}>
              <Text type="secondary" style={{ fontSize: '11px' }}>No data</Text>
            </div>
          )}
        </div>
      </div>

      {/* Visual separator */}
      <div style={{
        height: '1px',
        backgroundColor: '#f0f0f0',
        marginBottom: '12px',
        marginLeft: '-12px',
        marginRight: '-12px'
      }} />

      {/* Pacing hero (new view): bullet delivery bar with projection
          marker, delivering-vs-needed rate bars, and a budget donut.
          Classic view skips the whole block; impression bars are
          impression-campaigns-only (percentage campaigns are budget-paced,
          so they get the caption + donut but no impression bullet/rates). */}
      {!classicPacing && (impressionGoal > 0 || budget > 0) && (
        <div style={{ marginBottom: '12px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
            <Text style={{ fontSize: 11, color: '#595959' }}>
              Day {daysIntoFlight} of {flightLength}
              {impressionGoal > 0 ? ' · delivery vs goal' : ' · spend vs budget'}
            </Text>
            <Tooltip title={`Spent ${formatCurrency(overallTotalSpentForDonut, currencyFormat)} of ${formatCurrency(budget, currencyFormat)}`}>
              <BudgetDonut spentPct={spendPct} tone={pacingTone} />
            </Tooltip>
          </div>
          {impressionGoal > 0 && (
            <>
              <Tooltip
                title={
                  <div>
                    Fill = delivered to date · gray tick = 100% of goal ·
                    {' '}marker = projected finish ({flightTooYoung ? 'ramping' : `${formatNumber2SigFig(pace)}%`})
                  </div>
                }
              >
                <BulletBar deliveredPct={deliveredPct} projectionPct={pace} tone={pacingTone} height={6} />
              </Tooltip>
              <div style={{ marginTop: 8 }}>
                <RateBars delivering={pacingRatePerDay} needed={pacingNeededPerDay} tone={pacingTone} />
              </div>
            </>
          )}
        </div>
      )}

      {/* Enhanced Key Metrics - 5-column flexible layout */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        flexWrap: 'wrap', 
        gap: '8px',
        padding: '8px',
        backgroundColor: '#fafafa',
        borderRadius: '4px'
      }}>
        {/* Pace */}
        <div style={{ 
          flex: '1 1 calc(20% - 8px)', 
          minWidth: '60px',
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column'
        }}>
          <Text style={{ fontSize: '10px', color: '#595959', marginBottom: '2px', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {classicPacing ? 'Pace' : 'Projected finish'}
            {/* Classic escape hatch — same clock-icon pattern as the
                desktop Pacing header, so mobile-only users can reach the
                shared preference without a desktop session. */}
            {onToggleClassicPacing && (
              <Tooltip title="Classic pacing view — legacy 'Pace' display" mouseEnterDelay={0.3}>
                <Button
                  type="text"
                  size="small"
                  icon={<ClockCircleOutlined style={{ fontSize: 11 }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleClassicPacing();
                  }}
                  style={{
                    padding: '0 2px',
                    height: 18,
                    color: classicPacing ? '#1890ff' : '#8c8c8c',
                  }}
                />
              </Tooltip>
            )}
          </Text>
          <Tooltip title={classicPacing ? '' : pacingTooltip}>
            <Text
              style={{
                fontSize: '11px',
                color: flightTooYoung ? '#595959' : classicPacing ? '#1890ff' : PACING_TONES.ink,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                display: 'inline-flex',
                alignItems: 'center',
              }}
            >
              {!classicPacing && <PacingDot tone={pacingTone} />}
              {flightTooYoung ? '—' : pace ? `${formatNumber2SigFig(pace)}%` : 'N/A'}
              {!classicPacing && pacingTrend && !flightTooYoung && (
                <span style={{ marginLeft: 3, color: PACING_TONES.neutral }}>
                  {TREND_ARROWS[pacingTrend]}
                </span>
              )}
            </Text>
          </Tooltip>
          {!classicPacing && (
            <div style={{ width: '100%', marginTop: 4 }}>
              <PacingBar pace={pace} tooYoung={flightTooYoung} tone={pacingTone} height={4} />
            </div>
          )}
          {onOpenPacingModal && (
            <a
              style={{ fontSize: 11, color: '#1890ff', cursor: 'pointer', marginTop: 2 }}
              onClick={(e) => {
                e.stopPropagation();
                onOpenPacingModal(campaign);
              }}
            >
              View More
            </a>
          )}
        </div>

        {/* Spent */}
        <div style={{ 
          flex: '1 1 calc(20% - 8px)', 
          minWidth: '60px',
          display: 'flex', 
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column'
        }}>
          <Text style={{ fontSize: '10px', color: '#8c8c8c', marginBottom: '2px' }}>Spent</Text>
          <Text style={{ fontSize: '11px', fontWeight: '500' }}>
            {formatCurrency(overallTotalSpent, currencyFormat)}
          </Text>
        </div>

        {/* Orders - Conditionally shown based on column selection */}
        {showSales && (
          <div style={{ 
            flex: '1 1 calc(20% - 8px)', 
            minWidth: '60px',
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}>
            <Text style={{ fontSize: '10px', color: '#8c8c8c', marginBottom: '2px' }}>Orders</Text>
            <Text style={{ fontSize: '11px', fontWeight: '500' }}>
              {formatWholeNumber(transactionsCount)}
            </Text>
          </div>
        )}

        {/* Sign Ups - Conditionally shown based on column selection */}
        {showSignUps && (
          <div style={{ 
            flex: '1 1 calc(20% - 8px)', 
            minWidth: '60px',
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}>
            <Text style={{ fontSize: '10px', color: '#8c8c8c', marginBottom: '2px' }}>Sign Ups</Text>
            <Text style={{ fontSize: '11px', fontWeight: '500' }}>
              {formatWholeNumber(signUpsCount)}
            </Text>
          </div>
        )}

        {/* ROAS - Conditionally shown based on column selection */}
        {showSales && (
          <div style={{ 
            flex: '1 1 calc(20% - 8px)', 
            minWidth: '60px',
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}>
            <Text style={{ fontSize: '10px', color: '#8c8c8c', marginBottom: '2px' }}>ROAS</Text>
            <Text style={{ fontSize: '11px', fontWeight: '500' }}>
              {roas > 0 ? `${formatNumber2SigFig(roas)}%` : 'N/A'}
            </Text>
          </div>
        )}
      </div>

      {/* Additional Info - Pace and Days Remaining */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '8px' }}>
        {/* Days Remaining */}
        <Text type="secondary" style={{ fontSize: '11px' }}>
          {daysLeft > 0 ? `${daysLeft} days left` : 'Completed'}
        </Text>

        {/* Pace / projected finish */}
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <Tooltip title={classicPacing ? '' : pacingTooltip}>
            <Text strong style={{ fontSize: '11px', color: flightTooYoung ? '#595959' : classicPacing ? pacingColor : PACING_TONES.ink, fontVariantNumeric: 'tabular-nums', display: 'inline-flex', alignItems: 'center' }}>
              {!classicPacing && <PacingDot tone={pacingTone} />}
              {flightTooYoung ? '—' : `${formatNumber2SigFig(pace)}%`}
              {!classicPacing && pacingTrend && !flightTooYoung && (
                <span style={{ marginLeft: 3, color: PACING_TONES.neutral, fontWeight: 400 }}>{TREND_ARROWS[pacingTrend]}</span>
              )}
            </Text>
          </Tooltip>
        </div>
      </div>

      {/* Expanded Content - Phase 2 */}
      {isExpanded && (
        <div style={{ marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #f0f0f0' }}>
          {/* Cumulative delivered-vs-goal chart (new view) */}
          {!classicPacing && impressionGoal > 0 && aggregateData?.byDate?.length > 1 && (
            <div style={{ marginBottom: '12px' }}>
              <Text style={{ fontSize: 11, color: '#595959', display: 'block', marginBottom: 2 }}>
                Delivered vs goal pace — the gap at the end is the projected shortfall/surplus
              </Text>
              <CumulativePacingChart
                byDate={aggregateData.byDate}
                impressionGoal={impressionGoal}
                flightLength={flightLength}
                pace={pace}
                flightStart={startDate}
                flightEnd={endDate}
                tone={pacingTone}
              />
            </div>
          )}
          {/* Render selected columns in mobile-friendly format with fixed order */}
          {selectedTableColumns && possibleFields && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: isVerySmall ? '1fr' : '1fr 1fr', 
              gap: isVerySmall ? '12px' : '8px',
              fontSize: isVerySmall ? '13px' : '12px'
            }}>
              {(() => {
                // Define complete fixed column order: Start Date, End Date, Days Remaining, Performance, Sales, Sign Ups, Pacing, Budget, Attribution
                const fixedTitles = [
                  'Start Date', 'End Date', 'Days Remaining', 'Performance', 
                  'Sales', 'Sign Ups', 'Pacing', 'Budget', 'Attribution'
                ];
                const alreadyShownColumns = ['Name', 'Status', 'Impressions', 'Spent'];
                
                // Get fixed fields that are available to the user (in the selectedTableColumns)
                const fixedFields = fixedTitles
                  .map(title => {
                    const fieldKey = Object.keys(possibleFields).find(key => possibleFields[key].title === title);
                    return fieldKey && selectedTableColumns.includes(fieldKey) ? possibleFields[fieldKey] : null;
                  })
                  .filter(Boolean);
                
                // Get other selected fields (excluding fixed and already shown)
                const otherFields = selectedTableColumns
                  .map(key => possibleFields[key])
                  .filter(field => 
                    field && 
                    !fixedTitles.includes(field.title) && 
                    !alreadyShownColumns.includes(field.title)
                  );
                
                // Combine fixed fields with other fields
                const renderFields = [...fixedFields, ...otherFields];
                
                return renderFields.map((field, index) => (
                  <div key={field.title || index} style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    padding: '6px',
                    backgroundColor: '#f8f9fa',
                    borderRadius: '4px'
                  }}>
                    <Text strong style={{ fontSize: '11px', marginBottom: '2px' }}>
                      {field.title}
                    </Text>
                    {renderExpandedField(field, campaign)}
                  </div>
                ));
              })()}
            </div>
          )}
        </div>
      )}
    </Card>
  );
};

export default CampaignCard;
